/**
 * ActionMenu — Aktionsmenü für sekundäre Aktionen (Sprint-16-Brand).
 *
 * Das Menü liegt in einem Portal. Vorher hing es als `absolute top-full z-50` im
 * Dokumentfluss und wurde von jedem Vorfahren mit `overflow` abgeschnitten — `Table`
 * trägt `overflow-x-auto`, also traf das jede Aktionsspalte jeder Datentabelle. Ein
 * z-index bricht aus einem Overflow-Container nicht aus. `top-full` öffnete zudem immer
 * nach unten und lief am unteren Rand heraus; Radix' Kollisionserkennung klappt statt
 * dessen nach oben. Escape, Focus-Return zum Auslöser und Pfeiltasten-Navigation kommen
 * mit derselben Umstellung.
 *
 * `modal={false}` ist Absicht. Mehrere Einträge öffnen unmittelbar einen Dialog (die
 * Löschen-Einträge der Akte). Ein modales Menü sperrt währenddessen Body-Scroll und
 * Pointer-Events und übergibt diesen Zustand im selben Tick an den Dialog, der dasselbe
 * tut — genau die Naht, an der zwei Radix-Overlays sich gegenseitig aussperren. Ohne
 * Modalität bleibt außerdem der Seiten-Scroll frei, so wie vor dieser Änderung.
 *
 * Panel: bg-card + shadow-card + border-line/50. Destruktive Einträge in sealwax.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { type ReactNode } from 'react';
import { I, type IconName } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';

export type ActionMenuItem =
  | { separator: true }
  | {
      label: string;
      icon?: IconName;
      onClick: () => void;
      destructive?: boolean;
      disabled?: boolean;
      /** Native-Tooltip, v.a. um bei disabled-Items das Warum zu erklären (z.B. Locked-Beleg). */
      title?: string;
    };

interface ActionMenuProps {
  trigger: ReactNode;
  items: ActionMenuItem[];
  align?: 'left' | 'right';
  /**
   * Klasse für den Rahmen um den Auslöser. Nötig, weil der Rahmen `inline-block` ist: eine
   * Schaltfläche mit `w-full` blieb darin wirkungslos — auf einer Mobilkarte ist die volle
   * Breite aber genau die Trefferfläche, die man braucht.
   */
  className?: string;
}

export function ActionMenu({ trigger, items, align = 'right', className }: ActionMenuProps) {
  return (
    <DropdownMenu.Root modal={false}>
      {/* Der Rahmen bleibt ein eigenes Element, weil Aufrufstellen darüber `block w-full`
          setzen. Der Klick endet hier: sonst aktiviert das Öffnen des Menüs zugleich die
          Zeile oder Karte, in der es sitzt (§8). */}
      <div className={cn('inline-block', className)} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      </div>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align === 'right' ? 'end' : 'start'}
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            'border-line/50 bg-card shadow-card z-50 min-w-48 overflow-x-hidden rounded-md border',
            // Ein langes Menü scrollt in sich selbst, statt über den Fensterrand zu wachsen (§8).
            'max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto overscroll-contain',
          )}
        >
          {items.map((item, idx) =>
            'separator' in item ? (
              <DropdownMenu.Separator key={idx} className="border-line/50 my-1 border-t" />
            ) : (
              <DropdownMenu.Item
                key={idx}
                disabled={item.disabled}
                title={item.title}
                onSelect={() => item.onClick()}
                className={cn(
                  // 44 px nach §9: auf der Mobilkarte ist dieses Menü der einzige Weg zu den
                  // Zeilenaktionen. Ein Overlay wächst dabei nur in sich selbst und verschiebt
                  // kein Seitenlayout.
                  //
                  // `outline-none` statt eines Fokusrings: Radix gibt dem hervorgehobenen Eintrag
                  // echten DOM-Fokus, Maus wie Tastatur. Der Zustand ist `data-highlighted` und
                  // trägt dieselbe Fläche, die vorher `hover` gezeigt hat — ein zweiter Ring
                  // darüber wäre eine Optikänderung, keine Sichtbarkeit.
                  'flex min-h-11 w-full items-center gap-2.5 px-3 py-2 text-left text-sm outline-none transition-colors',
                  item.disabled
                    ? 'text-ink-faint cursor-not-allowed'
                    : item.destructive
                      ? 'text-sealwax hover:bg-sealwax/10 data-[highlighted]:bg-sealwax/10'
                      : 'text-ink-soft hover:bg-card-alt hover:text-ink data-[highlighted]:bg-card-alt data-[highlighted]:text-ink',
                )}
              >
                {item.icon && <I name={item.icon} size={14} />}
                <span>{item.label}</span>
              </DropdownMenu.Item>
            ),
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
